<rid-mechanic-background />

<rid-mechanic-wrapper>
  <rid-top-bar class="block w-full" name="Morfemenbord" [steps]="prompts().length" [progress]="progress()" />

  <rid-mechanic-content>
    @for (input of [rawPrompt()]; track input) {
      <div class="absolute grid justify-items-center gap-4" animate.leave="anim-fade-out">
        <div class="relative min-h-32 w-full">
          <div class="absolute bottom-0 grid w-full items-center justify-items-center gap-4" style="grid-template-columns: max-content 1fr">
            <rid-option
              #option
              animate.enter="wait-500 anim-pop-in"
              [option]="input.prompt"
              (animationend)="$event.animationName === 'pop-in' ? presentOption(option) : undefined"
            />

            <div class="relative w-full" animate.enter="wait-500 anim-pop-in">
              <div class="font-default absolute inset-x-0 bottom-full mb-2 text-center text-3xl text-white select-none">
                {{ input.stem }}
              </div>

              <rid-text-input class="w-full">
                <div class="flex items-center place-self-center">
                  <rid-vertical-spacer>
                    <rid-compressable-klank-card content="x" />
                  </rid-vertical-spacer>

                  <div class="grid flex-1 items-center" style="grid-template-columns: 1fr auto 1fr">
                    <div class="flex items-center justify-end">
                      @if (displayedPrefix(); as prefix) {
                        <rid-compressable-klank-card
                          #prefixCard
                          class="font-bold text-blue-500"
                          [content]="stripDashes(prefix)"
                          animate.enter="anim-pop-in"
                          ridSfx="BUBBLE_POP"
                        />
                      } @else if (input.prefix && step() === 'MORPHEMES') {
                        <div
                          class="border-surface-300 mx-1 inline-flex h-full min-w-16 items-center rounded-md border-3 border-dashed px-4 py-4 text-3xl text-slate-300 select-none"
                          animate.enter="anim-pop-in"
                        >
                          &nbsp;
                        </div>
                      }
                    </div>

                    <div class="flex items-center">
                      @for (k of keyboardComp.content(); track $index) {
                        <rid-syllabic-card #syllabicCard [value]="k" [highlightRule]="isStemChangingSuffix()" animate.enter="anim-pop-in" />
                      }
                      @for (k of keyboardComp.postfix(); track $index) {
                        <rid-syllabic-card
                          #syllabicCard
                          [value]="k"
                          [highlightRule]="isStemChangingSuffix()"
                          animate.enter="anim-fade-in"
                        />
                      }

                      @if (centerIndex() < center().length && keyboardComp.postfix().length === 0) {
                        <div
                          class="mx-1 inline-flex h-full items-center rounded-md border-3 px-4 py-4 text-3xl select-none"
                          [class]="
                            isStemChangingSuffix()
                              ? 'border-morpheme-stem text-morpheme-stem border-dotted font-bold'
                              : 'border-surface-300 border-dashed text-slate-300'
                          "
                          animate.enter="anim-pop-in"
                        >
                          ⋯
                        </div>
                      }
                    </div>

                    <div class="flex items-center justify-start">
                      @if (displayedSuffix(); as suffix) {
                        <rid-compressable-klank-card
                          #suffixCard
                          [class]="suffixCardClass()"
                          [content]="stripDashes(suffix)"
                          animate.enter="anim-pop-in"
                          ridSfx="BUBBLE_POP"
                        />
                      } @else if (input.suffix && step() === 'MORPHEMES') {
                        <div
                          class="border-surface-300 mx-1 inline-flex h-full min-w-16 items-center rounded-md border-3 border-dashed px-4 py-4 text-3xl text-slate-300 select-none"
                          animate.enter="anim-pop-in"
                        >
                          &nbsp;
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </rid-text-input>
            </div>
          </div>
        </div>

        <div class="grid w-full items-center gap-4" style="grid-template-columns: max-content 1fr">
          <!-- invisible clone of the prompt icon column, so the keyboard area lines up with the input row above -->
          <rid-option class="invisible" [option]="input.prompt" />

          <div class="grid">
            <div
              style="grid-area: 1 / 1"
              class="flex justify-center"
              [class.invisible]="!revealed() || step() !== 'MORPHEMES'"
              [class.pointer-events-none]="!revealed() || step() !== 'MORPHEMES'"
              [class.anim-pop-in]="revealed() && step() === 'MORPHEMES'"
            >
              <rid-morpheme-keyboard
                class="w-full"
                [prefixDisabled]="prefixKeyboardDisabled()"
                [suffixDisabled]="suffixKeyboardDisabled()"
                (prefixPressed)="onPrefixPressed($event)"
                (suffixPressed)="onSuffixPressed($event)"
              />
            </div>

            <div
              style="grid-area: 1 / 1"
              class="flex justify-center"
              [class.invisible]="!revealed() || step() !== 'CENTER'"
              [class.pointer-events-none]="!revealed() || step() !== 'CENTER'"
              [class.anim-pop-in]="revealed() && step() === 'CENTER'"
            >
              <rid-functional-syllabic-keyboard
                #keyboardComp
                [isDisabled]="isProvidingFeedback() || step() !== 'CENTER'"
                [layout]="centerLayout"
                mode="KLANK"
                [validate]="validateCenter.bind(this)"
                [correctValue]="center()[centerIndex()]"
                (inserted)="onCenterInsert()"
                (incorrected)="onCenterIncorrect()"
                (appliedRule)="onCenterApplyRule($event)"
              />
            </div>
          </div>
        </div>
      </div>
    }

    <!-- reveal -->
    @if (revelation(); as revelation) {
      <rid-modal-background [blur]="true" />
      <rid-correct-answer-modal (continue)="revealWaiter.finish()">
        <h3 class="font-heading mb-4 text-lg">Het goede antwoord is:</h3>
        <div class="rounded-md border-3 border-dashed border-slate-300">
          <div class="flex scale-75 place-self-center">
            @for (k of revelation; track $index) {
              <rid-syllabic-card [value]="k" />
            }
          </div>
        </div>
      </rid-correct-answer-modal>
    }

    <!-- feedback overlay -->
    <rid-overlay-feedback #overlayFeedback />
  </rid-mechanic-content>
</rid-mechanic-wrapper>
